// Wave AW.3-React (2026-05-16) — 외부 바이어 경매 목록 화면
// API: GET /buyer-app/auction/list?status=active&sort=&page=&limit=&q=

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { API } from '../../utils/api';
import { authBuyerFetch, isUnauthorized, handleUnauthorized } from './buyerApi';
import AuctionBuyerCard from './AuctionBuyerCard';
import AuctionBuyerLayout from './AuctionBuyerLayout';

var SORT_OPTIONS = [
  { key: 'ending_soon', label: '마감 임박' },
  { key: 'newest', label: '최신 등록' },
  { key: 'price_asc', label: '낮은 가격' },
  { key: 'price_desc', label: '높은 가격' },
];

function AuctionBuyerAuctionList() {
  var navigate = useNavigate();
  var [items, setItems] = useState([]);
  var [loading, setLoading] = useState(true);
  var [error, setError] = useState('');
  var [page, setPage] = useState(1);
  var [totalPages, setTotalPages] = useState(1);
  var [sort, setSort] = useState('ending_soon');
  var [q, setQ] = useState('');

  var load = useCallback(function() {
    setLoading(true);
    setError('');
    var params = new URLSearchParams({
      status: 'active',
      sort: sort,
      page: String(page),
      limit: '20',
    });
    if (q.trim()) params.append('q', q.trim());

    authBuyerFetch(API + '/buyer-app/auction/list?' + params.toString(), {})
      .then(function(r) {
        if (isUnauthorized(r)) { handleUnauthorized(navigate); throw new Error('unauthorized'); }
        return r.json();
      })
      .then(function(d) {
        if (d && d.success && d.data) {
          setItems(d.data.auctions || []);
          setTotalPages((d.data.pagination && d.data.pagination.totalPages) || 1);
        } else {
          setError((d && d.error) || '목록 로드 실패');
        }
        setLoading(false);
      })
      .catch(function(e) {
        if (e && e.message === 'unauthorized') return;
        setError('네트워크 오류');
        setLoading(false);
      });
  }, [sort, page, q, navigate]);

  useEffect(function() { load(); }, [load]);

  var handleSearch = function() { setPage(1); load(); };

  return (
    <AuctionBuyerLayout activeKey="auctions">
      <div style={{ maxWidth: 1280, margin: '0 auto', padding: '32px 32px 80px' }}>
        {/* 헤더 + 필터 */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
          <h2 style={{ fontSize: 22, fontWeight: 800, color: '#0f172a' }}>진행중 경매</h2>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {/* 정렬 */}
            <select value={sort} onChange={function(e){setSort(e.target.value); setPage(1);}}
              style={{
                height: 36, padding: '0 12px',
                border: '1.5px solid #e2e8f0', borderRadius: 8,
                background: 'white', fontSize: 13, fontWeight: 600,
                color: '#475569', cursor: 'pointer',
                fontFamily: "'Pretendard',sans-serif",
              }}>
              {SORT_OPTIONS.map(function(o){return <option key={o.key} value={o.key}>{o.label}</option>;})}
            </select>
            {/* 검색 */}
            <div style={{ display: 'flex', gap: 4 }}>
              <input type="text" value={q} onChange={function(e){setQ(e.target.value);}}
                onKeyDown={function(e){if(e.key==='Enter')handleSearch();}}
                placeholder="차종 검색"
                style={{
                  height: 36, padding: '0 14px',
                  border: '1.5px solid #e2e8f0', borderRadius: 8,
                  fontSize: 13, outline: 'none',
                  background: 'white',
                  fontFamily: "'Pretendard',sans-serif",
                  width: 180,
                }}
              />
              <button type="button" onClick={handleSearch}
                style={{
                  height: 36, padding: '0 14px',
                  border: 'none', borderRadius: 8,
                  background: '#1B3A5C', color: 'white',
                  fontSize: 13, fontWeight: 700, cursor: 'pointer',
                  fontFamily: "'Pretendard',sans-serif",
                }}>
                검색
              </button>
            </div>
          </div>
        </div>

        {error && (
          <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10, padding: 14, marginBottom: 20, color: '#991b1b', fontSize: 14 }}>
            ⚠ {error}
          </div>
        )}

        {loading ? (
          <div style={{ textAlign: 'center', padding: 60, color: '#94a3b8' }}>로딩 중...</div>
        ) : items.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 60, color: '#94a3b8' }}>
            <div style={{ fontSize: 40, opacity: 0.4, marginBottom: 12 }}>🔍</div>
            <div style={{ fontSize: 15, fontWeight: 600 }}>경매 매물이 없습니다</div>
            <div style={{ fontSize: 13, marginTop: 4 }}>검색어 또는 필터를 변경해보세요</div>
          </div>
        ) : (
          <>
            {/* 카드 그리드 — 반응형 */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
              gap: 16,
            }}>
              {items.map(function(auction) {
                return (
                  <AuctionBuyerCard key={auction.id} auction={auction}
                    onClick={function() { navigate('/buyer/auctions/' + auction.id); }}
                  />
                );
              })}
            </div>

            {/* 페이지네이션 */}
            {totalPages > 1 && (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 8, marginTop: 32 }}>
                <button type="button" disabled={page <= 1}
                  onClick={function(){setPage(page - 1);}}
                  style={{ ...pageBtnStyle, opacity: page <= 1 ? 0.4 : 1, cursor: page <= 1 ? 'not-allowed' : 'pointer' }}>
                  이전
                </button>
                <span style={{ fontSize: 14, fontWeight: 600, color: '#475569', padding: '0 12px' }}>
                  {page} / {totalPages}
                </span>
                <button type="button" disabled={page >= totalPages}
                  onClick={function(){setPage(page + 1);}}
                  style={{ ...pageBtnStyle, opacity: page >= totalPages ? 0.4 : 1, cursor: page >= totalPages ? 'not-allowed' : 'pointer' }}>
                  다음
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </AuctionBuyerLayout>
  );
}

var pageBtnStyle = {
  height: 36, padding: '0 16px',
  border: '1.5px solid #e2e8f0', borderRadius: 8,
  background: 'white', fontSize: 13, fontWeight: 600,
  color: '#475569',
  fontFamily: "'Pretendard',sans-serif",
};

export default AuctionBuyerAuctionList;
